import { addComponent, defineQuery, enterQuery } from "bitecs";
import * as THREE from "three";
//import { Object3D } from "three";
// import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { AssetLocation } from "../Assets";
import { AnimationComponent } from "../components/AnimationComponent";
import { ModelComponent } from "../components/ModelComponent";
import { PositionComponent } from "../components/PositionComponent";
import { TinyPetsWorld } from "../TinyPetsWorld";

export const createRenderSystem = (
    world: TinyPetsWorld,
    config: {
        camera: {
            fov?: number;
            aspect?: number;
            near?: number;
            far?: number;
            position: { x: number; y: number; z: number };
        };
        renderer: {
            height: number;
            width: number;
        };
    }
): {
    scene: THREE.Scene;
    camera: THREE.Camera;
    renderer: THREE.Renderer;
    renderSystem: (world: TinyPetsWorld) => TinyPetsWorld;
} => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
        config.camera.fov,
        config.camera.aspect,
        config.camera.near,
        config.camera.far
    );
    const gltfLoader = new GLTFLoader();

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffdefb, 0.2);
    hemiLight.position.set(0, 100, 0);
    scene.add(hemiLight);

    const directionalLight = new THREE.DirectionalLight(0xffdefb, 2);
    directionalLight.position.set(-20, 10, -10);
    scene.add(directionalLight);

    //scene.background = new THREE.Color(0xffdefb);

    //scene.fog = new THREE.Fog(scene.background, 1, 150);

    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(config.renderer.width, config.renderer.height);

    // SKYDOME
    const vertexShader =
        "varying vec3 vWorldPosition; void main() { vec4 worldPosition = modelMatrix * vec4( position, 1.0 ); vWorldPosition = worldPosition.xyz; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }";
    const fragmentShader =
        "uniform vec3 topColor; uniform vec3 bottomColor; uniform float offset; uniform float exponent; varying vec3 vWorldPosition; void main() { float h = normalize( vWorldPosition + offset ).y; gl_FragColor = vec4( mix( bottomColor, topColor, max( pow( max( h, 0.0 ), exponent ), 0.0 ) ), 1.0 ); }";

    const uniforms = {
        topColor: { value: new THREE.Color(0xfee8ff) },
        bottomColor: { value: new THREE.Color(0x2a1433) },
        offset: { value: 400 },
        exponent: { value: 0.6 },
    };

    const skyGeo = new THREE.SphereGeometry(500, 32, 15);
    const skyMat = new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: vertexShader!,
        fragmentShader: fragmentShader!,
        side: THREE.BackSide,
    });

    const sky = new THREE.Mesh(skyGeo, skyMat);
    scene.add(sky);

    // const controls = new OrbitControls(camera, renderer.domElement);
    // controls.target.set(0, 0, 0);

    camera.position.set(
        config.camera.position.x,
        config.camera.position.y,
        config.camera.position.z
    );
    camera.lookAt(0, 0, 0);

    // const axesHelper = new THREE.AxesHelper(100);
    // scene.add(axesHelper);

    const entityQuery = defineQuery([PositionComponent, ModelComponent]);
    const enteredQuery = enterQuery(entityQuery);

    const spawnModel = (eid: number) => {
        const assetLocation = AssetLocation[ModelComponent.assetLocation[eid]];
        const scale = ModelComponent.scale[eid];
        const rotation = {
            x: ModelComponent.rotation[eid][0],
            y: ModelComponent.rotation[eid][1],
            z: ModelComponent.rotation[eid][2],
        };

        const position = {
            x: PositionComponent.x[eid],
            y: PositionComponent.y[eid],
            z: PositionComponent.z[eid],
        };

        if (assetLocation.split(".").pop() === "glb") {
            // Load a glTF resource
            gltfLoader.load(
                assetLocation,
                (gltf: GLTF) => {
                    scene.add(gltf.scene);
                    gltf.scene.scale.multiplyScalar(scale);

                    gltf.scene.position.set(position.x, position.y, position.z);

                    gltf.scene.rotation.set(rotation.x, rotation.y, rotation.z);

                    gltf.scene.userData["eid"] = eid;

                    world.render.models[eid] = gltf;

                    if (gltf.animations.length > 0) {
                        addComponent(world, AnimationComponent, eid);

                        AnimationComponent.key[eid] = 1;
                    }
                },
                // called while loading is progressing
                (xhr) => {
                    /*console.log(
                                (xhr.loaded / xhr.total) * 100 + "% loaded"
                            );*/
                },
                // called when loading has errors
                (error) => {
                    console.log("An error happened", error);
                }
            );
        }
    };

    const updateModel = (eid: number) => {
        if (!world.render.models[eid]) {
            return;
        }

        world.render.models[eid].scene.position.set(
            PositionComponent.x[eid],
            PositionComponent.y[eid],
            PositionComponent.z[eid]
        );
        world.render.models[eid].scene.rotation.set(
            ModelComponent.rotation[eid][0],
            ModelComponent.rotation[eid][1],
            ModelComponent.rotation[eid][2]
        );
    };

    return {
        renderer: renderer,
        camera: camera,
        scene: scene,
        renderSystem: (world: TinyPetsWorld) => {
            enteredQuery(world).forEach(spawnModel);

            entityQuery(world).forEach(updateModel);

            // controls.update();
            renderer.render(scene, camera);

            return world;
        },
    };
};
